import { Injectable } from '@angular/core';
import { HttpClientService } from '@core/services/http-client/http-client.service';
import { API_URL_CONSTANT } from '@sharedModule/constants/api-url.constant';
import { DestinationCountry } from '@sharedModule/models/destination-country.model';
import { ResponseIdentity } from '@sharedModule/models/server-response-identity.model';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  MasterTransactionSettingsInterface,
  MasterPartnerTransactionSettingsInterface,
  PartnerTransactionSettingsInterface,
} from '../types/transaction-settings';
import {
  UpsertMasterTransactionSettingsInterface,
  UpsertMasterPartnerTransactionSettingsInterface,
  UpsertPartnerTransactionSettingsInterface,
} from '../types/upsert-transaction-settings';

type EndpointType =
  | 'source_of_income_codes'
  | 'remittance_purpose_codes'
  | 'transfer_types'
  | 'service_level_delivery_speed_codes';

@Injectable({
  providedIn: 'root',
})
export class TransactionSettingsService {
  private dialogDataSource = new BehaviorSubject<boolean>(false);
  dialogData$: Observable<boolean> = this.dialogDataSource.asObservable();
  constructor(private httpClient: HttpClientService) {}

  getMasterTransactionSettings(
    type: string
  ): Observable<ResponseIdentity<MasterTransactionSettingsInterface[]>> {
    const endpointMap: Record<EndpointType, string> = {
      source_of_income_codes:
        API_URL_CONSTANT.masterTransactionSettings.listSourceOfIncomes,
      remittance_purpose_codes:
        API_URL_CONSTANT.masterTransactionSettings.listPurposes,
      transfer_types:
        API_URL_CONSTANT.masterTransactionSettings.listTransferTypes,
      service_level_delivery_speed_codes:
        API_URL_CONSTANT.masterTransactionSettings.listServiceLevels,
    };

    const endpoint =
      type in endpointMap
        ? endpointMap[type as EndpointType]
        : API_URL_CONSTANT.masterTransactionSettings.listPaymentMethods;

    return this.httpClient.post(endpoint, {});
  }

  addMasterTransactionSetting(
    type: string,
    payload: UpsertMasterTransactionSettingsInterface
  ): Observable<ResponseIdentity<null>> {
    const endpointMap: Record<EndpointType, string> = {
      source_of_income_codes:
        API_URL_CONSTANT.masterTransactionSettings.addSourceOfIncome,
      remittance_purpose_codes:
        API_URL_CONSTANT.masterTransactionSettings.addPurpose,
      transfer_types:
        API_URL_CONSTANT.masterTransactionSettings.addTransferType,
      service_level_delivery_speed_codes:
        API_URL_CONSTANT.masterTransactionSettings.addServiceLevel,
    };

    const endpoint =
      type in endpointMap
        ? endpointMap[type as EndpointType]
        : API_URL_CONSTANT.masterTransactionSettings.addPaymentMethod;

    return this.httpClient.post(endpoint, payload);
  }

  updateMasterTransactionSetting(
    type: string,
    payload: UpsertMasterTransactionSettingsInterface
  ): Observable<ResponseIdentity<null>> {
    const endpointMap: Record<EndpointType, string> = {
      source_of_income_codes:
        API_URL_CONSTANT.masterTransactionSettings.updateSourceOfIncome,
      remittance_purpose_codes:
        API_URL_CONSTANT.masterTransactionSettings.updatePurpose,
      transfer_types:
        API_URL_CONSTANT.masterTransactionSettings.updateTransferType,
      service_level_delivery_speed_codes:
        API_URL_CONSTANT.masterTransactionSettings.updateServiceLevel,
    };

    const endpoint =
      type in endpointMap
        ? endpointMap[type as EndpointType]
        : API_URL_CONSTANT.masterTransactionSettings.updatePaymentMethod;

    return this.httpClient.post(endpoint, payload);
  }

  deleteMasterTransactionSetting(
    type: string,
    payload: {
      code: number;
    }
  ): Observable<ResponseIdentity<null>> {
    const endpointMap: Record<EndpointType, string> = {
      source_of_income_codes:
        API_URL_CONSTANT.masterTransactionSettings.deleteSourceOfIncome,
      remittance_purpose_codes:
        API_URL_CONSTANT.masterTransactionSettings.deletePurpose,
      transfer_types:
        API_URL_CONSTANT.masterTransactionSettings.deleteTransferType,
      service_level_delivery_speed_codes:
        API_URL_CONSTANT.masterTransactionSettings.deleteServiceLevel,
    };

    const endpoint =
      type in endpointMap
        ? endpointMap[type as EndpointType]
        : API_URL_CONSTANT.masterTransactionSettings.deletePaymentMethod;

    return this.httpClient.post(endpoint, payload);
  }

  setDialogData(data: boolean): void {
    this.dialogDataSource.next(data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTransactionSettings(): Observable<ResponseIdentity<any>> {
    return this.httpClient.post(
      API_URL_CONSTANT.masterPartnerTransactionSettings
        .getMasterTransactionSettings
    );
  }

  getMasterPartnerTransactionSettings(payload: {
    partner_id?: number;
    column?: string;
  }): Observable<ResponseIdentity<MasterPartnerTransactionSettingsInterface>> {
    return this.httpClient.post(
      API_URL_CONSTANT.masterPartnerTransactionSettings.getTransactionSettings,
      payload
    );
  }

  addMasterPartnerTransactionSetting(
    payload: UpsertMasterPartnerTransactionSettingsInterface
  ): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.masterPartnerTransactionSettings.addTransactionSetting,
      payload
    );
  }

  updateMasterPartnerTransactionSetting(
    payload: UpsertMasterPartnerTransactionSettingsInterface
  ): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.masterPartnerTransactionSettings
        .updateTransactionSetting,
      payload
    );
  }

  deleteMasterPartnerTransactionSetting(payload: {
    partner_id: number;
    id: number;
    column: string;
    code: number;
  }): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.masterPartnerTransactionSettings
        .deleteTransactionSetting,
      payload
    );
  }

  getDestinationCountryDropdown(payload: {
    src_country: string;
  }): Observable<ResponseIdentity<DestinationCountry>> {
    return this.httpClient.post(
      API_URL_CONSTANT.common.getDestinationCountryList,
      payload
    );
  }

  getPartnerTransactionSettings(payload: {
    src_country: string;
    dest_country: string;
    column?: string;
  }): Observable<ResponseIdentity<PartnerTransactionSettingsInterface>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partnerTransactionSettings.getTransactionSettings,
      payload
    );
  }

  addPartnerTransactionSetting(
    payload: UpsertPartnerTransactionSettingsInterface
  ): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partnerTransactionSettings.addTransactionSetting,
      payload
    );
  }

  updatePartnerTransactionSetting(
    payload: UpsertPartnerTransactionSettingsInterface
  ): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partnerTransactionSettings.updateTransactionSetting,
      payload
    );
  }

  deletePartnerTransactionSetting(payload: {
    id: number;
    column: string;
    code: number;
  }): Observable<ResponseIdentity<null>> {
    return this.httpClient.post(
      API_URL_CONSTANT.partnerTransactionSettings.deleteTransactionSetting,
      payload
    );
  }
}
